/**
 * The capture-device model shared by main, preload and renderer (ticket 11,
 * FR-41/FR-42).
 *
 * Only the renderer can *see* devices — `navigator.mediaDevices` lives in the
 * web context — but the choice of device belongs to the main process along with
 * the rest of application state. So the renderer enumerates and reports; this
 * module holds every rule that acts on the report, pure and testable in Node:
 * what a device is called, which one is the system default, which one a
 * remembered choice resolves to once hardware has come and gone, what to say
 * about it, and the constraints the capture will eventually be opened with.
 *
 * It is also the validation boundary. Everything here arrives from a renderer
 * over IPC, so the entry points take `unknown` and discard what does not fit
 * rather than trusting the shape.
 */

/** The two input kinds cuekards captures. Outputs are not cuekards' business.
 * Deliberately *not* named `MediaDeviceKind`: lib.dom already owns that name
 * and includes `audiooutput`, and these files also handle real
 * `MediaDeviceInfo` values. */
export type CaptureKind = "videoinput" | "audioinput";

/** Both kinds, in the order they are presented. */
export const CAPTURE_KINDS: readonly CaptureKind[] = [
  "videoinput",
  "audioinput",
];

/** How each kind is named in user-facing text. Generated labels capitalise it,
 * so there is one spelling of "camera" in the app rather than two. */
export const CAPTURE_KIND_NOUN: Record<CaptureKind, string> = {
  videoinput: "camera",
  audioinput: "microphone",
};

/**
 * Chromium's pseudo-devices. On Windows every microphone is enumerated three
 * times — `default`, `communications`, and its own hashed id — all naming the
 * same hardware, so showing them all would list one mic as three. They are kept
 * in the model because `default` is the useful capture target (it *follows* the
 * OS default rather than pinning today's answer), but hidden from the picker,
 * where "System default" is already the first option.
 */
const PSEUDO_DEVICE_IDS: readonly string[] = ["default", "communications"];

/** The pseudo-device that tracks the OS setting. */
const DEFAULT_DEVICE_ID = "default";

/** Labels Chromium prefixes onto its pseudo-devices ("Default - Realtek"),
 * stripped when the device is shown as the system default so the text does not
 * read "System default (Default - Realtek)". */
const PSEUDO_LABEL_PREFIX = /^(Default|Communications)\s+-\s+/;

/** One enumerated input device, with a label guaranteed to be non-empty. */
export interface MediaDevice {
  deviceId: string;
  kind: CaptureKind;
  label: string;
}

/** A remembered device override. The label is stored alongside the id for two
 * reasons: a device that has since been unplugged can still be *named* in the
 * fallback message, and it is the second key the device is matched on — see
 * {@link resolveDevice}. */
export interface DeviceChoice {
  deviceId: string;
  label: string;
}

/** A capture resolution/framerate override. Every field is optional and an
 * absent field means "whatever the device does natively" (FR-42). */
export interface CaptureFormat {
  width?: number;
  height?: number;
  frameRate?: number;
}

/** The persisted device settings: an override per kind plus an optional format.
 * Empty means "system defaults, native format". */
export interface DeviceSelection {
  choices?: Partial<Record<CaptureKind, DeviceChoice>>;
  format?: CaptureFormat;
}

/** How a resolution was reached. `label` is the interesting one: Chromium's
 * `deviceId` is a per-origin salted hash, so it can rotate between a dev build
 * and a packaged one, or when site data is cleared. Matching the remembered
 * label as a second key keeps the same physical microphone selected instead of
 * silently degrading to the default forever. */
export type DeviceMatch = "id" | "label" | "default" | "none";

/** What a remembered choice resolves to against the devices actually present. */
export interface ResolvedDevice {
  kind: CaptureKind;
  /** The device that will be opened, or null when the kind has none. */
  device: MediaDevice | null;
  /** The system default for this kind, whether or not it was selected — the
   * picker names it on its "System default" option. */
  systemDefault: MediaDevice | null;
  /** The remembered override, retained even while it is absent so replugging
   * restores it rather than stranding the user on the default. */
  requested?: DeviceChoice;
  how: DeviceMatch;
  /** True when {@link requested} was asked for but could not be found — i.e.
   * the resolved device is not the one the user chose. */
  fallback: boolean;
}

/** The device state both windows are views onto, held in the main process
 * (PRD §8) and broadcast on every change. */
export interface DeviceState {
  /** Every input device present, in enumeration order (FR-41). */
  devices: MediaDevice[];
  /** The remembered overrides and format, as persisted. */
  selection: DeviceSelection;
  /** What the selection resolves to against the devices present now. */
  resolved: Record<CaptureKind, ResolvedDevice>;
  /** Messages about anything that did not resolve as asked — surfaced as soon
   * as the device vanishes, not when record is pressed. */
  notices: string[];
  /** False until the renderer has enumerated once, so the UI can say "looking
   * for devices…" rather than "no camera detected". */
  enumerated: boolean;
  /**
   * How many cameras the browser admits exist — counted from the **raw**
   * enumeration, before {@link normaliseDevices} has been near it, and meaningless
   * until {@link enumerated}.
   *
   * It is a separate number from `devices.filter(videoinput).length` and it has to
   * be. A browser answers `enumerateDevices` before media permission with one
   * entry per device carrying a blank id and a blank label — it admits what is
   * *there* while refusing to say what it *is* — and `normaliseDevices` drops
   * those, correctly, because a device with no id cannot be opened by it. So the
   * normalised list cannot tell "there is no camera" from "not allowed to look
   * yet", and both of the things that depend on the answer get it wrong: the web's
   * green room says "No camera found" to a first-time visitor, and
   * {@link captureConstraints} asks for a camera on a machine that has none and
   * loses the microphone with it.
   */
  cameras: number;
  /**
   * The devices the user has switched off in the control bar, in
   * {@link CAPTURE_KINDS} order (web-ui ticket 06).
   *
   * Deliberately *not* part of {@link DeviceState.selection}, and so deliberately
   * not persisted: which camera you prefer is a setting, and having turned yours
   * off for one take is not. It comes back on the next visit, the way it does in
   * every video call.
   *
   * It lives on the state rather than in the page holding the button, because the
   * take is opened a worker away and {@link captureConstraints} is the one rule
   * both openers read. A microphone switched off in front of a self-view has to
   * be one the recorder does not name either — and the two disagreeing would not
   * merely record the sound the room promised it would not, it would close and
   * reopen the hardware at "action", because a take may borrow the preview only
   * while the two constraint objects deep-equal.
   */
  off: CaptureKind[];
}

/** The capture constraints a resolved selection produces, handed to
 * `getUserMedia` by the recorder in ticket 12. */
export interface CaptureConstraints {
  /**
   * Absent when there is no camera to open, and present in every other case —
   * including "there is one and nothing in particular was chosen", which is `{}`.
   *
   * The distinction has to exist because `getUserMedia` is all-or-nothing: a
   * request that names video on a machine with no camera rejects outright and
   * opens *neither* device, so a take that could have been audio also fails, and
   * the failure it reports is about a camera.
   */
  video?: MediaTrackConstraints;
  /**
   * Absent when the microphone has been switched off (web-ui ticket 06), and
   * present in every other case.
   *
   * Optional for a narrower reason than {@link CaptureConstraints.video} is. A
   * machine with no microphone is not a case this product has an answer for — a
   * prompter that cannot hear you is not one — so this side never goes away
   * because the hardware is missing. It goes away because somebody pressed the
   * button on the bar, and then it has to go away here too, or the take records
   * the sound the room said it would not.
   */
  audio?: MediaTrackConstraints;
}

/** Whether a `MediaDeviceInfo.kind` string is one cuekards captures. */
export function isCaptureKind(kind: unknown): kind is CaptureKind {
  return kind === "videoinput" || kind === "audioinput";
}

/** Whether a device id is one of Chromium's pseudo-devices rather than a piece
 * of hardware the user would recognise. */
export function isPseudoDevice(deviceId: string): boolean {
  return PSEUDO_DEVICE_IDS.includes(deviceId);
}

/** A device's name with any pseudo-device prefix removed. */
export function displayLabel(device: MediaDevice): string {
  return device.label.replace(PSEUDO_LABEL_PREFIX, "");
}

function generatedLabel(kind: CaptureKind, n: number): string {
  const noun = CAPTURE_KIND_NOUN[kind];
  return `${noun[0]!.toUpperCase()}${noun.slice(1)} ${n}`;
}

function asRawDevice(
  value: unknown,
): { deviceId: string; kind: CaptureKind; label: string } | null {
  if (typeof value !== "object" || value === null) return null;
  const { deviceId, kind, label } = value as Record<string, unknown>;
  if (typeof deviceId !== "string" || deviceId === "") return null;
  if (!isCaptureKind(kind)) return null;
  return { deviceId, kind, label: typeof label === "string" ? label : "" };
}

/**
 * Turn a raw enumeration into the device list the rest of the app uses: input
 * kinds only, no blank ids, no duplicate ids within a kind, and every device
 * carrying a name a human recognises. Labels come back empty until media
 * permission has been granted, so an unlabelled device is numbered within its
 * kind ("Camera 2") rather than shown as an empty row.
 *
 * Takes `unknown[]` because the enumeration crosses IPC from a renderer:
 * anything that is not a device of a kind cuekards captures is dropped.
 */
export function normaliseDevices(raw: readonly unknown[]): MediaDevice[] {
  const devices: MediaDevice[] = [];
  const seen = new Set<string>();
  const counts: Record<CaptureKind, number> = {
    videoinput: 0,
    audioinput: 0,
  };
  for (const value of raw) {
    const device = asRawDevice(value);
    if (!device) continue;
    const key = `${device.kind}:${device.deviceId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const n = ++counts[device.kind];
    const label = device.label.trim();
    devices.push({
      deviceId: device.deviceId,
      kind: device.kind,
      label: label === "" ? generatedLabel(device.kind, n) : label,
    });
  }
  return devices;
}

/** Every device of one kind, in enumeration order — pseudo-devices included. */
export function devicesOfKind(
  devices: readonly MediaDevice[],
  kind: CaptureKind,
): MediaDevice[] {
  return devices.filter((d) => d.kind === kind);
}

/** The devices offered in the picker: real hardware only, since "System
 * default" is already an option in its own right (FR-41 — one microphone must
 * appear once, not once per Chromium alias). */
export function selectableDevices(
  devices: readonly MediaDevice[],
  kind: CaptureKind,
): MediaDevice[] {
  return devicesOfKind(devices, kind).filter(
    (d) => !isPseudoDevice(d.deviceId),
  );
}

/**
 * The system default for a kind (FR-42). Chromium exposes an explicit `default`
 * pseudo-device for audio that tracks the OS setting — preferred when present
 * because it keeps following the OS afterwards. Video has no such entry, so the
 * first enumerated camera stands in, which is what `getUserMedia` would pick.
 */
export function defaultDevice(
  devices: readonly MediaDevice[],
  kind: CaptureKind,
): MediaDevice | null {
  const ofKind = devicesOfKind(devices, kind);
  return (
    ofKind.find((d) => d.deviceId === DEFAULT_DEVICE_ID) ?? ofKind[0] ?? null
  );
}

/**
 * Resolve a remembered override against the devices present right now, by id
 * first and by label second (see {@link DeviceMatch}). An override that cannot
 * be found either way falls back to the default and raises
 * {@link ResolvedDevice.fallback}, so the user is told before they press record
 * rather than by a failure when they do.
 */
export function resolveDevice(
  devices: readonly MediaDevice[],
  kind: CaptureKind,
  requested: DeviceChoice | undefined,
): ResolvedDevice {
  const systemDefault = defaultDevice(devices, kind);
  if (!requested) {
    return {
      kind,
      device: systemDefault,
      systemDefault,
      how: systemDefault ? "default" : "none",
      fallback: false,
    };
  }
  const ofKind = devicesOfKind(devices, kind);
  const byId = ofKind.find((d) => d.deviceId === requested.deviceId);
  const byLabel = ofKind.find(
    (d) => !isPseudoDevice(d.deviceId) && d.label === requested.label,
  );
  const match = byId ?? byLabel;
  if (match) {
    return {
      kind,
      device: match,
      systemDefault,
      requested,
      how: byId ? "id" : "label",
      fallback: false,
    };
  }
  return {
    kind,
    device: systemDefault,
    systemDefault,
    requested,
    how: systemDefault ? "default" : "none",
    fallback: true,
  };
}

/**
 * The message shown when a resolution is not what the user asked for, or null
 * when it is. Written to be actionable at the moment it appears: it names the
 * device that has gone and the one standing in for it.
 */
export function deviceNotice(resolved: ResolvedDevice): string | null {
  const noun = CAPTURE_KIND_NOUN[resolved.kind];
  if (resolved.fallback && resolved.requested) {
    const gone = `The ${noun} “${resolved.requested.label}” is not connected`;
    return resolved.device
      ? `${gone} — using “${displayLabel(resolved.device)}” instead.`
      : `${gone}, and no other ${noun} was found.`;
  }
  if (!resolved.device) return `No ${noun} detected.`;
  return null;
}

function positive(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}

/**
 * Narrow an unvalidated format from a renderer to the fields that make sense,
 * or null when none do. Width and height only count as a pair — half a
 * resolution is not a resolution.
 */
export function sanitiseFormat(value: unknown): CaptureFormat | null {
  if (typeof value !== "object" || value === null) return null;
  const { width, height, frameRate } = value as Record<string, unknown>;
  const format: CaptureFormat = {};
  const w = positive(width);
  const h = positive(height);
  if (w && h) {
    format.width = w;
    format.height = h;
  }
  const fps = positive(frameRate);
  if (fps) format.frameRate = fps;
  return Object.keys(format).length > 0 ? format : null;
}

/**
 * Video capture constraints for a resolved device (FR-42). With no format
 * override the only constraint is the device itself, so capture runs at the
 * device's native resolution and framerate. An override is expressed as
 * `ideal` rather than `exact`: a mode the camera cannot do degrades to the
 * nearest it can, instead of throwing when record is pressed.
 */
export function videoConstraints(
  device: MediaDevice | null,
  format: CaptureFormat | undefined,
): MediaTrackConstraints {
  const constraints: MediaTrackConstraints = {};
  if (device) constraints.deviceId = { exact: device.deviceId };
  const clean = sanitiseFormat(format);
  if (clean?.width) constraints.width = { ideal: clean.width };
  if (clean?.height) constraints.height = { ideal: clean.height };
  if (clean?.frameRate) constraints.frameRate = { ideal: clean.frameRate };
  return constraints;
}

/** Audio capture constraints for a resolved device. Format overrides are
 * video-only, so this pins the device and nothing else. */
export function audioConstraints(
  device: MediaDevice | null,
): MediaTrackConstraints {
  return device ? { deviceId: { exact: device.deviceId } } : {};
}

/**
 * The constraints a capture will be opened with, for a whole device state
 * (web-ui ticket 05).
 *
 * One function rather than three lines wherever they are needed, because there
 * are now two openers and they have to agree *structurally*. The core worker
 * opens a take through `DeviceController.getConstraints`; the page holds the
 * camera open before the take through `CaptureEngine.preview`, and the engine
 * lets a take borrow the already-open device only when the two deep-equal. A
 * second copy of these lines would work until it drifted by one key, and the
 * failure would not be an error — it would be the camera closing and reopening
 * at the end of the countdown, a light blink and a black frame at "action".
 */
export function captureConstraints(state: DeviceState): CaptureConstraints {
  // A camera known not to be there is left out rather than asked for as "any
  // camera". `{}` means "there is one and nothing in particular was chosen";
  // absent means there is nothing to open, and `getUserMedia` being
  // all-or-nothing is what makes the difference matter — see
  // {@link CaptureConstraints.video}.
  const noCamera = state.enumerated && state.cameras === 0;
  // A device the user switched off is left out by the same mechanism, and that
  // is the point: "there is no camera" and "you turned the camera off" are two
  // different sentences on screen and one instruction to the hardware.
  const off = new Set(state.off);
  return {
    ...(noCamera || off.has("videoinput")
      ? {}
      : {
          video: videoConstraints(
            state.resolved.videoinput.device,
            state.selection.format,
          ),
        }),
    ...(off.has("audioinput")
      ? {}
      : { audio: audioConstraints(state.resolved.audioinput.device) }),
  };
}

/**
 * Whether a take opened on these constraints would open anything at all (web-ui
 * ticket 06).
 *
 * One function because two places ask it and they must not drift: the control bar
 * dims the record button and says why, and `RecordingController.start` refuses
 * the take that the `R` key and the spoken "start recording" reach without going
 * near a button. A bar that said yes over a controller that said no would be a
 * live record button that does nothing.
 *
 * Null is "nobody has enumerated yet", and the safe answer to that is yes: a take
 * refused because the page had not finished looking would be a button that is
 * dead on arrival for no reason the user can see.
 */
export function namesADevice(constraints: CaptureConstraints | null): boolean {
  return (
    constraints === null ||
    constraints.video !== undefined ||
    constraints.audio !== undefined
  );
}

/**
 * How many cameras a raw enumeration says exist.
 *
 * Counts entries rather than identities, which is the only reading that means
 * anything before media permission — see {@link DeviceState.cameras}. Takes
 * `unknown[]` for the reason {@link normaliseDevices} does: it crosses IPC.
 */
export function countCameras(raw: readonly unknown[]): number {
  return raw.filter(
    (device) =>
      typeof device === "object" &&
      device !== null &&
      (device as { kind?: unknown }).kind === "videoinput",
  ).length;
}

/** Resolution choices offered in the picker; absent means the device's native
 * resolution (FR-42). */
export const RESOLUTION_PRESETS: readonly {
  label: string;
  width: number;
  height: number;
}[] = [
  { label: "1920 × 1080", width: 1920, height: 1080 },
  { label: "1280 × 720", width: 1280, height: 720 },
  { label: "640 × 480", width: 640, height: 480 },
];

/** Framerate choices offered in the picker; absent means native. */
export const FRAME_RATE_PRESETS: readonly number[] = [24, 30, 60];
